"use client";

import { useEffect, useRef } from "react";
import { TransitionLink } from "@/components/shell/TransitionLink";
import { MarketOverlay } from "@/components/market/MarketOverlay";
import { Status } from "@/components/ui/Status";
import { Term } from "@/components/ui/Term";
import { models, press, products } from "@/content/facts";
import { contactNavItem } from "@/content/site";
import { home, homeH1Parts } from "@/content/home";
import { whatsappUrl } from "@/content/whatsapp";
import { worldBus } from "@/lib/world-bus";
import { clamp, smoothstep } from "@/world/core/math";
import styles from "./HomeFilm.module.css";

// El inicio: un solo plano secuencia controlado por el scroll (guía 7.3 y 8.1). Un
// escenario fijo dentro de una sección alta: el scroll mueve la cámara y los textos de
// cada bloque aparecen y se van compuestos con la escena. Sin JavaScript, el mismo texto
// se lee en orden, como una página normal.
//
// El <h1> es uno solo y está repartido: la palabra en el bloque 1 y el claim en el 2.
//
// Cuánto scroll lleva cada tramo del guion, en alturas de pantalla: [u final, pantallas].
// Casi todos son un plano entero; el súper (plano 6) va en tramos más finos porque ahí
// pasan varias cosas seguidas (la vidriera, el pasillo, el celular, Tajy, la salida) y
// cada una necesita su tiempo de lectura. El recorrido completo son ~22 pantallas: entre
// 60 y 90 segundos de scroll normal (guía 7.3).
// Ajuste 2026-10-18: se recortaron los tramos donde la cámara viaja sin texto (el final
// de "piensa", antes del cielo; la salida del súper; antes de los medios). Con el largo
// anterior había que deslizar más de una pantalla sin ver ningún cambio y parecía trabado.
const BASE_SEGMENTS: [number, number][] = [
  [1, 1.0],
  [2, 1.6],
  [3, 1.4],
  [4, 2.2],
  [5, 2.1],
  [5.44, 1.3],
  [5.6, 0.6],
  [5.7, 0.45],
  [5.875, 1.3],
  [5.965, 1.2],
  [6.16, 0.6],
  [7, 1.2],
  [8, 1.2],
  [9, 1.3],
  [10, 1.3],
];
// Todo el recorrido después de la apertura se estira (pedido 2026-10-18: con ~22
// pantallas la historia pasaba demasiado rápido, sobre todo en iPhone). El primer tramo
// no se toca: ahí aparecen los accesos de las nubes con muy poco scroll.
const STRETCH = 1;
const SEGMENTS: [number, number][] = BASE_SEGMENTS.map(([u, len], i) => [u, i === 0 ? len : len * STRETCH]);
const TOTAL = SEGMENTS.reduce((a, [, len]) => a + len, 0);
/** Inicio de cada tramo: en u y en pantallas acumuladas. */
const SEG_U0 = SEGMENTS.map((_, i) => (i === 0 ? 0 : SEGMENTS[i - 1][0]));
const SEG_P0 = SEGMENTS.map((_, i) => SEGMENTS.slice(0, i).reduce((a, [, len]) => a + len, 0));

/** Aparece entre a0 y a1, se va entre b0 y b1 (en unidades del guion). */
const fade = (u: number, a0: number, a1: number, b0: number, b1: number) =>
  smoothstep(a0, a1, u) * (1 - smoothstep(b0, b1, u));

// Con movimiento reducido la cámara no viaja: cada bloque tiene su plano fijo y el
// scroll pasa de uno a otro (otra composición, quieta; guía 6).
const HOLDS: [number, number][] = [
  [1.05, 0],
  [2.1, 2],
  [3.0, 2.9],
  [4.1, 3.95],
  [5.3, 5],
  [5.7, 5.5],
  [5.88, 5.84],
  [6.2, 5.93],
  [6.95, 6.6],
  [8.2, 7.8],
  [9.3, 9],
  [Infinity, 10],
];
const holdFor = (u: number) => HOLDS.find(([until]) => u < until)![1];


type Block = {
  id: string;
  /** Visibilidad del bloque en función de u. */
  alpha: (u: number) => number;
  /** Cuánto se oscurece la escena detrás (máscara de legibilidad). */
  shade: number;
  /** u al que lleva el foco del teclado (el bloque queda a la vista). */
  focusU: number;
};

// Accesos del plano de nubes (solo celular): primero lo que busca quien llega por un negocio.
const CLOUD_NAV = [
  { href: "/productos", label: "Productos" },
  { href: "/empresa", label: "Empresa" },
  { href: "/investigacion", label: "Investigación" },
  { href: "/modelos", label: "Modelos" },
];

const BLOCKS: Block[] = [
  // Plano 2, solo nubes: entre la palabra que se va y el claim que llega.
  // Solo existe en celular (CSS). Aparece casi apenas se desliza (u 0,12 ≈ un séptimo de
  // pantalla, completo a media pantalla): debajo de la palabra, sin pisarla.
  { id: "nav", alpha: (u) => fade(u, 0.12, 0.42, 1.4, 1.56), shade: 0.3, focusU: 0.8 },
  { id: "what", alpha: (u) => fade(u, 2.3, 2.5, 2.95, 3.03), shade: 0.75, focusU: 2.75 },
  { id: "thinks", alpha: (u) => fade(u, 3.02, 3.18, 3.98, 4.08), shade: 0.7, focusU: 3.5 },
  { id: "figure", alpha: (u) => fade(u, 3.86, 3.92, 4.0, 4.1), shade: 0.75, focusU: 3.95 },
  { id: "sky", alpha: (u) => fade(u, 4.52, 4.62, 4.82, 4.88), shade: 0.6, focusU: 4.7 },
  { id: "punch", alpha: (u) => fade(u, 4.93, 4.98, 5.08, 5.16), shade: 0.4, focusU: 5 },
  // Plano 6: el título frente a la vidriera, Yvyra con el celular, Tajy con su cámara.
  { id: "products", alpha: (u) => fade(u, 5.45, 5.49, 5.525, 5.548), shade: 0.6, focusU: 5.5 },
  { id: "yvyra", alpha: (u) => fade(u, 5.775, 5.8, 5.862, 5.874), shade: 0.6, focusU: 5.83 },
  { id: "tajy", alpha: (u) => fade(u, 5.89, 5.905, 5.955, 5.968), shade: 0.55, focusU: 5.93 },
  { id: "open", alpha: (u) => fade(u, 6.3, 6.4, 6.85, 6.95), shade: 0.75, focusU: 6.6 },
  { id: "media", alpha: (u) => fade(u, 7.44, 7.54, 8.04, 8.12), shade: 0.5, focusU: 7.8 },
  { id: "next", alpha: (u) => fade(u, 8.5, 8.62, 9.05, 9.14), shade: 0.7, focusU: 8.85 },
  { id: "close", alpha: (u) => fade(u, 9.45, 9.6, 99, 100), shade: 0.75, focusU: 9.8 },
];

export function HomeFilm() {
  const { word, continuation } = homeH1Parts();
  const sectionRef = useRef<HTMLElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const wordRef = useRef<HTMLSpanElement>(null);
  const lineRef = useRef<HTMLParagraphElement>(null);
  const claimRef = useRef<HTMLSpanElement>(null);
  const fineRef = useRef<HTMLDivElement>(null);
  const counterRef = useRef<HTMLParagraphElement>(null);
  const oursMarkRef = useRef<HTMLSpanElement>(null);
  const crowdMarkRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const stage = stageRef.current;
    if (!section || !stage) return;
    let raf = 0;
    let top = 0;
    let span = 1;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const measure = () => {
      top = section.getBoundingClientRect().top + window.scrollY;
      span = Math.max(1, section.offsetHeight - stage.offsetHeight);
      const w = wordRef.current;
      if (w) {
        worldBus.wordBox = {
          left: w.offsetLeft,
          top: w.offsetTop,
          fontSize: parseFloat(getComputedStyle(w).fontSize),
        };
      }
    };

    const uFromScroll = () => {
      const p = clamp((window.scrollY - top) / span) * TOTAL;
      let i = 0;
      while (i < SEGMENTS.length - 1 && p >= SEG_P0[i + 1]) i++;
      const [u1, len] = SEGMENTS[i];
      return SEG_U0[i] + clamp((p - SEG_P0[i]) / len) * (u1 - SEG_U0[i]);
    };
    const scrollFor = (u: number) => {
      let i = 0;
      while (i < SEGMENTS.length - 1 && u >= SEGMENTS[i][0]) i++;
      const [u1, len] = SEGMENTS[i];
      const p = SEG_P0[i] + clamp((u - SEG_U0[i]) / (u1 - SEG_U0[i])) * len;
      return top + (p / TOTAL) * span;
    };
    // Para las capturas: posición de scroll que corresponde a un `u` del guion.
    (window as unknown as { __homeScrollFor?: (u: number) => number }).__homeScrollFor = scrollFor;

    const rect = (el: HTMLElement | null, id: string, shade: number) => {
      if (!el || shade < 0.02) {
        worldBus.setRect(id, null);
        return;
      }
      const r = el.getBoundingClientRect();
      worldBus.setRect(id, { x: r.left, y: r.top, w: r.width, h: r.height, shade });
    };

    // Con el teclado: si el foco entra en un bloque que no se ve, el scroll lo trae (la
    // cámara llega con su transición, nunca con un corte).
    const onFocus = (e: FocusEvent) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>("[data-block]");
      if (!el) return;
      const b = BLOCKS.find((x) => x.id === el.dataset.block);
      if (!b) return;
      const cur = worldBus.uRendered;
      if (b.alpha(cur) > 0.6) return;
      window.scrollTo({ top: scrollFor(b.focusU), behavior: "auto" });
    };
    stage.addEventListener("focusin", onFocus);

    // Los bloques se buscan una vez en el DOM (data-block): no hace falta un ref por bloque.
    const blockEls = new Map<string, HTMLElement>();
    stage.querySelectorAll<HTMLElement>("[data-block]").forEach((el) => blockEls.set(el.dataset.block ?? "", el));
    let lastCount = -1;
    const frame = () => {
      raf = requestAnimationFrame(frame);
      const raw = uFromScroll();
      worldBus.u = reduced.matches ? holdFor(raw) : raw;
      // Los textos siguen a la cámara que se ve (con su inercia), no al scroll crudo.
      const u = reduced.matches ? raw : worldBus.status === "ready" ? worldBus.uRendered : raw;
      const s = stage.style;
      const lineA = 1 - smoothstep(0.5, 0.85, u);
      const wordA = 1 - smoothstep(0.78, 1.08, u);
      const hintA = 1 - smoothstep(0.02, 0.1, u);
      const claimA = fade(u, 1.56, 1.76, 2.08, 2.24);
      const fineA = fade(u, 1.66, 1.84, 2.08, 2.24);
      s.setProperty("--a-line", lineA.toFixed(3));
      s.setProperty("--a-word", wordA.toFixed(3));
      s.setProperty("--a-hint", hintA.toFixed(3));
      s.setProperty("--a-claim", claimA.toFixed(3));
      s.setProperty("--a-fine", fineA.toFixed(3));
      s.setProperty("--u", u.toFixed(4));
      rect(lineRef.current, "home-line", lineA * 0.7);
      rect(claimRef.current, "home-claim", claimA * 0.85);
      rect(fineRef.current, "home-fine", fineA * 0.9);
      claimRef.current?.toggleAttribute("data-off", claimA < 0.3);
      fineRef.current?.toggleAttribute("data-off", fineA < 0.3);

      for (const b of BLOCKS) {
        const el = blockEls.get(b.id);
        if (!el) continue;
        const a = b.alpha(u);
        el.style.setProperty("--a", a.toFixed(3));
        // Lo que no se ve no recibe clics (sí foco: el teclado lo trae a la vista).
        el.toggleAttribute("data-off", a < 0.3);
        rect(el, `home-${b.id}`, a * b.shade);
      }

      // El remate del cielo: un anillo y un nombre sobre el punto nuestro, otro sobre el
      // resto (el centro de las familias que se ven). Se lee solo: todos allá, uno acá.
      const marksA = worldBus.status === "ready" ? fade(u, 4.9, 4.95, 5.08, 5.13) : 0;
      const ours = worldBus.oursScreen;
      const oursEl = oursMarkRef.current;
      if (oursEl) {
        oursEl.style.opacity = ours ? marksA.toFixed(3) : "0";
        if (ours) {
          oursEl.style.transform = `translate(${ours.x.toFixed(1)}px, ${ours.y.toFixed(1)}px)`;
          // Cerca del borde derecho, el nombre va del otro lado del anillo.
          oursEl.toggleAttribute("data-flip", ours.x > window.innerWidth - 300);
        }
      }
      const crowdEl = crowdMarkRef.current;
      if (crowdEl) {
        let sx = 0;
        let sy = 0;
        let sw = 0;
        for (const f of worldBus.familyScreen) {
          if (!f?.visible) continue;
          sx += f.x * f.r;
          sy += f.y * f.r;
          sw += f.r;
        }
        crowdEl.style.opacity = sw > 0 ? marksA.toFixed(3) : "0";
        if (sw > 0) crowdEl.style.transform = `translate(${(sx / sw).toFixed(1)}px, ${(sy / sw).toFixed(1)}px)`;
      }

      // Contador discreto de vueltas, sincronizado con la escena.
      const n = worldBus.status === "ready" ? worldBus.loopCount : Math.min(7, Math.max(0, Math.floor((u - 3.06) / (0.8 / 7)) + 1));
      if (n !== lastCount && counterRef.current) {
        lastCount = n;
        counterRef.current.dataset.n = String(n);
        const num = counterRef.current.querySelector("[data-num]");
        if (num) num.textContent = String(Math.max(1, n));
        counterRef.current.toggleAttribute("data-verified", n >= 7 && u >= 3.85);
      }
    };

    measure();
    raf = requestAnimationFrame(frame);
    const ro = new ResizeObserver(measure);
    ro.observe(section);
    ro.observe(stage);
    window.addEventListener("resize", measure);
    document.fonts?.ready.then(measure).catch(() => undefined);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      stage.removeEventListener("focusin", onFocus);
      window.removeEventListener("resize", measure);
      ["home-line", "home-claim", "home-fine", ...BLOCKS.map((b) => `home-${b.id}`)].forEach((id) => worldBus.setRect(id, null));
      worldBus.wordBox = null;
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className={styles.film}
      style={{ "--scroll": `${TOTAL * 100}svh` } as React.CSSProperties}
      aria-label="Presentación"
    >
      <div ref={stageRef} className={styles.stage}>
        {/* Bloques 1 y 2 · Apertura y claim */}
        <h1 className={styles.h1}>
          <span ref={wordRef} className={styles.word}>
            {word.slice(0, -2)}
            <span className={styles.wordAi}>{word.slice(-2)}</span>
          </span>
          <span className="sr-only">, </span>
          <span ref={claimRef} className={styles.claim}>
            {continuation}
          </span>
        </h1>
        <p ref={lineRef} className={styles.line}>
          {home.opening.line}
        </p>
        <div ref={fineRef} className={styles.fine}>
          <p>{home.claim.fineprint}</p>
          <TransitionLink href={home.claim.cta.href} className={styles.cta}>
            {home.claim.cta.label}
          </TransitionLink>
        </div>
        <span className={styles.hint} aria-hidden="true">
          <span className={styles.hintLine} />
        </span>

        {/* Plano 2 · Accesos sobre las nubes (solo celular, ver el CSS). */}
        <nav data-block="nav" className={`${styles.block} ${styles.cloudNav}`} aria-label="Accesos rápidos">
          <ul className={styles.cloudNavList}>
            {CLOUD_NAV.map((item, i) => (
              <li key={item.href} style={{ "--i": i } as React.CSSProperties}>
                <TransitionLink href={item.href} className={styles.cloudLink}>
                  {item.label}
                </TransitionLink>
              </li>
            ))}
            <li className={styles.cloudWide} style={{ "--i": CLOUD_NAV.length } as React.CSSProperties}>
              <TransitionLink href={contactNavItem.href} className={`${styles.cloudLink} ${styles.cloudContact}`}>
                {contactNavItem.label}
              </TransitionLink>
            </li>
          </ul>
        </nav>
        {/* El súper (plano 6): la conversación del celular y la vista de Tajy, encima
            de la escena. */}
        <MarketOverlay />

        {/* Bloque 3 · Qué es */}
        <div data-block="what" className={`${styles.block} ${styles.what}`}>
          <p className={styles.lead}>{home.what.body}</p>
          <p className={styles.second}>{home.what.second}</p>
        </div>

        {/* Bloque 4 · Piensa antes de responder */}
        <div data-block="thinks" className={`${styles.block} ${styles.thinks}`}>
          <h2 className={styles.h2}>{home.thinks.title}</h2>
          <p className={styles.body}>{home.thinks.body}</p>
          <p ref={counterRef} className={styles.counter} aria-hidden="true">
            <span>{home.thinks.counter}</span> <span data-num>1</span>
            <span className={styles.of}> / 7</span>
            <span className={styles.verifiedTag}>{home.thinks.verified}</span>
          </p>
        </div>
        <div data-block="figure" className={`${styles.block} ${styles.figure}`}>
          <p className={styles.figureValue} data-raw="forced:1,7">{home.thinks.figure}</p>
          <p className={styles.figureLabel}>
            {home.thinks.figureLabel.before}
            <Term help={home.thinks.termHelp}>{home.thinks.figureLabel.term}</Term>
            {home.thinks.figureLabel.after}
          </p>
          <TransitionLink href={home.thinks.cta.href} className={styles.cta}>
            {home.thinks.cta.label}
          </TransitionLink>
        </div>

        {/* Bloque 5 · El cielo de modelos */}
        <div data-block="sky" className={`${styles.block} ${styles.sky}`}>
          <p className={styles.figureValue}>{home.sky.figure}</p>
          <p className={styles.body}>{home.sky.body}</p>
        </div>
        <span ref={oursMarkRef} className={`${styles.mark} ${styles.markOurs}`} aria-hidden="true">
          <span className={styles.markText}>{home.sky.marks.ours}</span>
        </span>
        <span ref={crowdMarkRef} className={`${styles.mark} ${styles.markCrowd}`} aria-hidden="true">
          <span className={styles.markText}>{home.sky.marks.crowd}</span>
        </span>
        <div data-block="punch" className={`${styles.block} ${styles.punch}`}>
          <p className={styles.punchText}>{home.sky.punch}</p>
          <TransitionLink href={home.sky.cta.href} className={styles.cta}>
            {home.sky.cta.label}
          </TransitionLink>
        </div>

        {/* Bloque 6 · Productos */}
        <div data-block="products" className={`${styles.block} ${styles.products}`}>
          <p className="label">{home.products.eyebrow}</p>
          <h2 className={styles.h2}>{home.products.title}</h2>
        </div>
        <div data-block="yvyra" className={`${styles.block} ${styles.product} ${styles.yvyra}`}>
          <div className={styles.productHead}>
            <h3 className={styles.h3}>{products.yvyra.name}</h3>
            <Status kind={products.yvyra.status.kind}>{products.yvyra.status.label}</Status>
          </div>
          <p className={styles.body}>{home.products.yvyra}</p>
        </div>
        <div data-block="tajy" className={`${styles.block} ${styles.product} ${styles.tajy}`}>
          <div className={styles.productHead}>
            <h3 className={styles.h3}>{products.tajy.name}</h3>
            <Status kind={products.tajy.status.kind}>{products.tajy.status.label}</Status>
          </div>
          <p className={styles.body}>{home.products.tajy}</p>
          <TransitionLink href={home.products.cta.href} className={styles.cta}>
            {home.products.cta.label}
          </TransitionLink>
        </div>

        {/* Bloque 7 · Abierto */}
        <div data-block="open" className={`${styles.block} ${styles.open}`}>
          <h2 className={styles.h2}>{home.open.title}</h2>
          <p className={styles.body}>{home.open.body}</p>
          <ul className={styles.objects}>
            {home.open.links.map((l) => {
              const external = l.href.startsWith("http");
              const inner = (
                <>
                  <span className={styles.objectLabel}>{l.label}</span>
                  <span className={styles.objectDetail}>{l.detail}</span>
                </>
              );
              return (
                <li key={l.href}>
                  {external ? (
                    <a className={styles.object} href={l.href} target="_blank" rel="noopener">
                      {inner}
                    </a>
                  ) : (
                    <TransitionLink className={styles.object} href={l.href}>
                      {inner}
                    </TransitionLink>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        {/* Bloque 8 · En los medios */}
        <div data-block="media" className={`${styles.block} ${styles.media}`}>
          <p className="label">{home.media.eyebrow}</p>
          <ul className={styles.outlets}>
            {[...press].reverse().map((p) => (
              <li key={p.url}>
                <a href={p.url} target="_blank" rel="noopener" className={styles.outlet}>
                  <span className={styles.outletName}>{p.label}</span>
                  <time className={styles.outletDate} dateTime={p.isoDate}>
                    {p.date}
                  </time>
                </a>
              </li>
            ))}
          </ul>
          <TransitionLink href={home.media.cta.href} className={styles.cta}>
            {home.media.cta.label}
          </TransitionLink>
        </div>

        {/* Bloque 9 · Lo que viene */}
        <div data-block="next" className={`${styles.block} ${styles.next}`}>
          <h2 className={styles.h2}>{home.next.title}</h2>
          <ul className={styles.lines}>
            {models.map((m) => (
              <li key={m.id} data-kind={m.status.kind}>
                {m.line}
              </li>
            ))}
          </ul>
          <TransitionLink href={home.next.cta.href} className={styles.cta}>
            {home.next.cta.label}
          </TransitionLink>
        </div>

        {/* Bloque 10 · Cierre */}
        <div data-block="close" className={`${styles.block} ${styles.close}`}>
          <h2 className={styles.h2}>{home.close.title}</h2>
          <p className={styles.body}>{home.close.body}</p>
          <div className={styles.closeActions}>
            <a className={styles.primary} href={whatsappUrl("general")} target="_blank" rel="noopener">
              {home.close.primary}
            </a>
            <TransitionLink href={home.close.secondary.href} className={styles.cta}>
              {home.close.secondary.label}
            </TransitionLink>
          </div>
        </div>
      </div>
    </section>
  );
}
